/**
 * THE CHEER MODEL — a GYM-PARTNERSHIP MACHINE, not a championship line.
 *
 * Pure JS, no React, so `verify-spaces.mjs` asserts every rule here directly
 * rather than through a rendered component.
 *
 * WHY CHEER IS ITS OWN ANIMAL (Brian, 2026-08-06). Every other space is one
 * flat list of organizations. Cheer is a HIERARCHY and a PARTNERSHIP funnel at
 * the same time:
 *
 *     GYM  ──has~10──▶  TEAM  ──wins many──▶  CHAMPIONSHIP
 *      │
 *      └─ the CONTRACT and the EXCLUSIVE BRANDED JEWELRY PROGRAM live HERE
 *
 * So the space is GYM-CENTRIC. Teams and titles are nested and AGGREGATED under
 * the gym they belong to; the partnership — a signed contract plus an exclusive,
 * fully-branded jewelry program delivered to that gym — is a pipeline the gym
 * moves along; and the championship pursuits, which are the actual revenue
 * engine, roll UP from the gym's teams rather than living beside them.
 *
 * THE JOB IS THE WHOLE TAM. Every cheer gym in the universe is either a PARTNER
 * or a TARGET, and the point of the space is to turn targets into partners. That
 * is why a gym with nothing recorded still renders, loudly, as a target — an
 * empty row here is the work, not a data problem.
 *
 * WHAT IS REAL AND WHAT IS INFERRED (stated, never blurred):
 *
 *  - The GYM is a real HubSpot company (the `cheer` pursuit line).
 *  - The CONTRACT / PURSUIT is a real associated DEAL — same model as the
 *    Community Hero nomination and the Greek product pursuit, for the same
 *    reason: a deal natively has an owner, a stage and a date.
 *  - The TEAM layer does NOT exist as an object in HubSpot. It is DERIVED here
 *    by reading each cheer championship's crowned team against the gym names —
 *    "Cheer Athletics Panthers" under the gym "Cheer Athletics". Every link
 *    carries the `basis` it was made on, every gym reports how its titles were
 *    matched, and titles that match no gym land in an UNMATCHED bucket that the
 *    space shows rather than silently drops. Nothing is invented: a gym with no
 *    matched title reports zero, not "unknown".
 */

/** The space kind. One switch, declared on the category AND on the backend config. */
export const CHEER_PARTNERSHIP = 'cheer-partnership'

/** What a partnership IS — both halves, stated once, shown everywhere. */
export const PARTNERSHIP_DEFINITION =
  'A partnership is BOTH halves: a signed contract AND an exclusive, fully-branded jewelry program delivered to the gym. One without the other is not a partner.'

/* ---- The partnership pipeline --------------------------------------- */

/**
 * THE FOUR STATES A GYM CAN BE IN. Ordered, so the board columns, the sort and
 * the pathway all read off one list and can never disagree about what comes
 * after what.
 *
 * `pursuit` sits between target and contract because an open partnership deal
 * is a real, different state from an untouched gym — it is the difference
 * between "nobody has called them" and "we are mid-negotiation".
 */
export const PARTNERSHIP_STAGES = [
  {
    key: 'target', step: 1, label: 'Target', color: '#8b8b8b',
    hint: 'in the TAM, no partnership pursuit open yet',
  },
  {
    key: 'pursuit', step: 2, label: 'Partnership in pursuit', color: '#3f6f9a',
    hint: 'a partnership deal is open — the contract is not signed',
  },
  {
    key: 'contract', step: 3, label: 'Contract signed', color: '#a5762a',
    hint: 'signed — the exclusive branded jewelry program is not live yet',
  },
  {
    key: 'partner', step: 4, label: 'Partner — jewelry live', color: '#2f9e5f',
    hint: 'signed contract AND an exclusive fully-branded jewelry program',
  },
]

export const PARTNERSHIP_BY_KEY = Object.fromEntries(PARTNERSHIP_STAGES.map((s) => [s.key, s]))
const STAGE_ORDER = Object.fromEntries(PARTNERSHIP_STAGES.map((s, i) => [s.key, i]))

/** Where a stage key sorts. An unknown key sorts last rather than first. */
export function partnershipOrder(key) {
  return STAGE_ORDER[key] ?? PARTNERSHIP_STAGES.length
}

const str = (v) => (v == null ? '' : String(v).trim())

/** Stage words that mean the contract is DEAD, not merely unsigned. */
const LOST = /(lost|declined|dead|no\s*bid|closed\s*lost|withdraw)/i
/** Stage words that mean the paper is actually SIGNED. */
const SIGNED = /(signed|executed|closed\s*won|won|partner|active|onboard)/i

/**
 * Is this gym's partnership deal a SIGNED CONTRACT?
 *
 * `won` comes from HubSpot's own `hs_is_closed_won` flag when the portal has it
 * (the backend resolves it), which is the only trustworthy answer. The stage
 * word is the fallback, and an unrecognized word means NOT SIGNED — the
 * conservative direction, because calling an unsigned gym a partner is the one
 * error this space must never make.
 */
export function contractSigned(deal) {
  if (!deal) return false
  if (deal.won === true) return true
  if (deal.lost === true) return false
  const s = `${str(deal.status)} ${str(deal.stage)}`
  if (LOST.test(s)) return false
  return SIGNED.test(s)
}

const JEWELRY_LIVE = /(live|launched|delivered|shipped|active|in\s*market|complete)/i
const JEWELRY_BUILD = /(design|build|proof|artwork|development|in\s*progress|pending)/i

/**
 * THE EXCLUSIVE BRANDED JEWELRY PROGRAM — the second half of a partnership, and
 * an ATTRIBUTE OF THE GYM rather than a deal of its own.
 *
 * Read from whatever the data actually records, in priority order: an explicit
 * status on the gym, then the partnership deal's own jewelry status. A gym with
 * nothing recorded is `live: false` and says "not launched" — never "unknown",
 * because an unlaunched program and an unrecorded one are the same job.
 */
export function jewelryMeta(record) {
  const raw =
    str(record?.jewelry?.status) ||
    str(record?.jewelryStatus) ||
    str(record?.partnershipDeal?.jewelryStatus)
  const exclusive = record?.jewelry?.exclusive === true || /exclusive/i.test(raw)
  if (!raw) {
    return { present: false, live: false, building: false, exclusive, label: 'Not launched', color: 'var(--faint)' }
  }
  const live = JEWELRY_LIVE.test(raw)
  const building = !live && JEWELRY_BUILD.test(raw)
  return {
    present: true,
    live,
    building,
    exclusive,
    label: raw,
    color: live ? '#2f9e5f' : building ? '#a5762a' : '#8b8b8b',
  }
}

/**
 * THE GYM'S PARTNERSHIP STATE — one stage, decided from the deal and the
 * jewelry program together, never from either alone.
 *
 * A LOST deal returns the gym to TARGET, flagged `reopened`: the next move
 * there is to go again, not to stare at the loss.
 */
export function partnershipMeta(record) {
  const deal = record?.partnershipDeal || null
  const jewelry = jewelryMeta(record)
  const signed = contractSigned(deal)
  const lost = !!deal && (deal.lost === true || LOST.test(`${str(deal.status)} ${str(deal.stage)}`))

  let key = 'target'
  if (signed) key = jewelry.live ? 'partner' : 'contract'
  else if (deal && !lost) key = 'pursuit'

  const stage = PARTNERSHIP_BY_KEY[key]
  return {
    key,
    label: stage.label,
    color: stage.color,
    hint: stage.hint,
    step: stage.step,
    order: partnershipOrder(key),
    deal,
    dealId: str(deal?.id),
    signed,
    lost,
    reopened: key === 'target' && lost,
    jewelry,
    // A PARTNER is both halves. Named separately from the stage key so no
    // consumer has to remember which of the four stages counts as partnered.
    partner: key === 'partner',
    // The deal's own words, kept — the pipeline's stage is the record.
    stageLabel: str(deal?.status) || str(deal?.stage),
    owner: str(deal?.owner),
    openedAt: deal?.pursuitDate || null,
    signedAt: signed ? (deal?.wonDate || null) : null,
  }
}

/** Counts by partnership stage, in pipeline order — the TAM coverage rollup. */
export function partnershipRollup(records) {
  const tally = Object.fromEntries(PARTNERSHIP_STAGES.map((s) => [s.key, 0]))
  for (const r of records || []) tally[partnershipMeta(r).key]++
  return PARTNERSHIP_STAGES.map((s) => ({ ...s, count: tally[s.key] }))
}

/* ---- Gym → team → championship ------------------------------------- */

/**
 * IS THIS CHAMPIONSHIP A CHEER TITLE? Keyed off the sport the championship
 * record actually records, with the event name as a fallback for records whose
 * sport field is blank (which is common — see the data-gap note in CLAUDE.md).
 *
 * Deliberately narrow: "cheer", "spirit" and "stunt" are the cheer family.
 * Dance is NOT included — a dance title under a cheer gym is a real thing, but
 * inferring one from the word alone would pull in unrelated events.
 */
const CHEER_SPORT = /\b(cheer|cheerleading|spirit|stunt)\b/i

export function isCheerChampionship(record) {
  if (!record) return false
  if (CHEER_SPORT.test(str(record.sport))) return true
  return !str(record.sport) && CHEER_SPORT.test(str(record.name))
}

/** Every cheer title in a championship universe. */
export function cheerChampionships(records) {
  return (records || []).filter(isCheerChampionship)
}

/**
 * NAME NORMALIZATION — the whole basis of the derived team layer, so it is one
 * function and it is boring on purpose: lowercase, reduce anything that isn't a
 * letter or a digit to a single space, drop a leading "the". Both sides of every
 * comparison go through it, so it can never match one spelling and miss another.
 */
export function normalizeName(v) {
  return str(v)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^the /, '')
}

/**
 * A gym name has to be DISTINCTIVE enough to match on. One short token ("elite",
 * "top") would match half the universe, so it is refused — an unmatched title is
 * honest, a wrongly-attributed one is not.
 */
function matchable(norm) {
  if (!norm) return false
  const tokens = norm.split(' ')
  return tokens.length >= 2 || norm.length >= 6
}

/**
 * Does this crowned-team string belong to this gym, and if so what is the TEAM?
 *
 * Three bases, most-trusted first:
 *   `exact`  — the champion IS the gym ("Cheer Athletics"): a gym-level title.
 *   `prefix` — "Cheer Athletics Panthers": team = Panthers. This is the normal
 *              all-star spelling and the reason the derived layer works at all.
 *   `token`  — the gym name appears as whole tokens elsewhere in the string
 *              ("Panthers (Cheer Athletics)"): team = whatever is left.
 *
 * Returns null when there is no match. Substring matching is never used: "ace"
 * must not match "Acevedo".
 */
export function matchTeam(championNorm, gymNorm) {
  if (!championNorm || !matchable(gymNorm)) return null
  if (championNorm === gymNorm) return { basis: 'exact', team: '' }
  if (championNorm.startsWith(gymNorm + ' ')) {
    return { basis: 'prefix', team: championNorm.slice(gymNorm.length + 1).trim() }
  }
  const padded = ` ${championNorm} `
  const idx = padded.indexOf(` ${gymNorm} `)
  if (idx >= 0) {
    const rest = (padded.slice(1, idx + 1) + padded.slice(idx + gymNorm.length + 2)).replace(/\s+/g, ' ').trim()
    return { basis: 'token', team: rest }
  }
  return null
}

/** The crowned team on a championship record — '' when nobody has been crowned. */
export function crownedTeam(title) {
  return str(title?.champion) || str(title?.winningSchool)
}

/** Title-case a normalized team fragment back into something readable. */
function titleCase(norm) {
  return str(norm).split(' ').filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/**
 * THE ROLLUP — cheer titles attached to the gyms whose teams won them.
 *
 * @param gyms   adapted cheer gym records (the pursuit plane)
 * @param titles championship records (the 2-64425303 plane), any sport
 * @returns `{ byGym, unmatched, cheerTotal, matchedTotal }` — `byGym` is keyed
 *          by the gym's record id and carries the derived TEAM layer.
 *
 * Every title lands in exactly ONE place: under a gym, or in `unmatched`. That
 * conservation is what makes the space's counts trustworthy — the board can
 * report "412 cheer titles, 260 linked to a gym, 152 not yet linkable" instead
 * of quietly showing only what matched.
 */
export function rollupCheer(gyms = [], titles = []) {
  const cheer = cheerChampionships(titles)
  const byGym = {}
  const unmatched = []

  // Index the gyms by every token in their normalized name, so a champion
  // string only ever compares against gyms that share a word with it.
  const index = new Map()
  const entries = []
  for (const g of gyms) {
    const norm = normalizeName(g.name)
    if (!matchable(norm)) continue
    const entry = { gym: g, norm }
    entries.push(entry)
    for (const t of new Set(norm.split(' '))) {
      if (!index.has(t)) index.set(t, [])
      index.get(t).push(entry)
    }
  }

  for (const title of cheer) {
    const champion = crownedTeam(title)
    const championNorm = normalizeName(champion)
    let best = null
    if (championNorm) {
      const seen = new Set()
      for (const tok of new Set(championNorm.split(' '))) {
        for (const entry of index.get(tok) || []) {
          if (seen.has(entry)) continue
          seen.add(entry)
          const hit = matchTeam(championNorm, entry.norm)
          // The LONGEST gym name wins: "Cheer Athletics Frisco" beats "Cheer
          // Athletics" for a team that names both.
          if (hit && (!best || entry.norm.length > best.entry.norm.length)) best = { entry, hit }
        }
      }
    }
    if (!best) {
      unmatched.push({ title, champion })
      continue
    }
    const id = best.entry.gym.id
    const bucket = byGym[id] || (byGym[id] = { titles: [], teams: new Map(), bases: new Set() })
    const teamName = best.hit.team ? titleCase(best.hit.team) : ''
    bucket.bases.add(best.hit.basis)
    bucket.titles.push({ title, team: teamName, basis: best.hit.basis, champion })
    const key = teamName || '__gym__'
    if (!bucket.teams.has(key)) {
      bucket.teams.set(key, { name: teamName, gymLevel: !teamName, titles: [] })
    }
    bucket.teams.get(key).titles.push(title)
  }

  const out = {}
  for (const [id, b] of Object.entries(byGym)) {
    const teams = [...b.teams.values()]
      .map((t) => ({
        ...t,
        titleCount: t.titles.length,
        // Both are already real Dates on a normalized championship record —
        // nothing is parsed or invented here.
        lastTitleAt: t.titles.reduce((m, x) => {
          const d = x.crownedAt instanceof Date ? x.crownedAt : (x.effDate instanceof Date ? x.effDate : null)
          return d && (!m || d > m) ? d : m
        }, null),
      }))
      .sort((a, b2) => b2.titleCount - a.titleCount || String(a.name).localeCompare(String(b2.name)))
    out[id] = {
      titles: b.titles,
      teams,
      titleCount: b.titles.length,
      teamCount: teams.length,
      lastTitleAt: teams.reduce((m, t) => (t.lastTitleAt && (!m || t.lastTitleAt > m) ? t.lastTitleAt : m), null),
      basis: [...b.bases].join(','),
    }
  }

  return {
    byGym: out,
    unmatched,
    cheerTotal: cheer.length,
    matchedTotal: cheer.length - unmatched.length,
  }
}

/**
 * The gym records with their derived team / title rollup attached, so every
 * shared surface — the card, the table, the sorter, the CSV, the drawer — reads
 * `titleCount` / `teams` like any other field with no new plumbing.
 *
 * A gym with no matched title gets a REAL ZERO and an empty team list. That is
 * the honest answer and it is also the sort key that surfaces the untouched
 * end of the TAM.
 */
export function attachCheerRollup(gyms = [], titles = []) {
  const roll = rollupCheer(gyms, titles)
  const records = gyms.map((g) => {
    const hit = roll.byGym[g.id]
    const meta = partnershipMeta(g)
    return {
      ...g,
      teams: hit?.teams || [],
      titles: hit?.titles || [],
      titleCount: hit?.titleCount || 0,
      teamCount: hit?.teamCount || 0,
      lastTitleAt: hit?.lastTitleAt || null,
      titleBasis: hit?.basis || '',
      // THE PARTNERSHIP STATE, FLATTENED. The shared comparator, the shared
      // table and the CSV read these like any other column — which is what
      // keeps `lib/championships.js` from having to know what a cheer
      // partnership is. `partnershipRank` is the pipeline ORDER, so sorting by
      // it walks target → pursuit → contract → partner.
      partnershipStage: meta.key,
      partnershipLabel: meta.label,
      partnershipRank: meta.order,
      partner: meta.partner,
      contractSigned: meta.signed,
      jewelryLive: meta.jewelry.live,
      jewelryLabel: meta.jewelry.label,
    }
  })
  return { records, rollup: roll }
}

/* ---- The create payload --------------------------------------------- */

const ymd = (d) =>
  d instanceof Date && !Number.isNaN(d.getTime())
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : ''

/** What a cheer partnership deal can be opened FOR. The deal name says which. */
export const CHEER_PROGRAMS = [
  'Exclusive branded jewelry program',
  'Championship rings',
  'Team & athlete jewelry',
  'Gym-branded collection',
  'Senior / graduating athlete program',
]

/** `Cheer Partnership — <Gym>`. One canonical spelling; the backend composes it too. */
export function partnershipDealName(record) {
  return `Cheer Partnership — ${str(record?.name) || 'Unnamed gym'}`
}

/**
 * The payload a partnership pursuit posts. BUSINESS NAMES ONLY — no HubSpot
 * pipeline id and no `dealstage`, because the client cannot know them and
 * inventing one writes the deal into the wrong pipeline. The backend resolves
 * both. `companyId` is the RAW HubSpot company id, not the namespaced record id.
 */
export function partnershipDealPayload(record, { program = '', owner = '', ownerId = '', pursuitDate = null } = {}) {
  const p = str(program) || CHEER_PROGRAMS[0]
  return {
    kind: CHEER_PARTNERSHIP,
    line: record?.lineKey || '',
    companyId: record?.sourceId || '',
    organization: record?.name || '',
    dealname: partnershipDealName(record),
    program: p,
    product: p,
    status: 'To Pursue',
    owner: str(owner),
    ownerId: str(ownerId),
    pursuitDate: pursuitDate instanceof Date ? ymd(pursuitDate) : str(pursuitDate),
    note: PARTNERSHIP_DEFINITION,
  }
}

/** Everything missing before a partnership payload could be sent — [] when complete. */
export function partnershipProblems(payload) {
  const out = []
  if (!payload?.companyId) out.push('no HubSpot company id on the gym — the deal has nothing to associate to')
  if (!payload?.dealname) out.push('no deal name')
  if (!payload?.program) out.push('no program selected')
  return out
}

/**
 * The record patch that reflects a just-created partnership deal, so the board
 * moves the gym from TARGET to IN PURSUIT the moment the create returns.
 *
 * Honest by construction: it records the deal we know we just wrote and NOTHING
 * else. No signed contract, no jewelry program, no won date — opening a pursuit
 * is not a partnership, and the real stage arrives on the next load.
 */
export function optimisticCheerPatch(payload, result) {
  return {
    partnershipDeal: {
      id: String(result?.id || ''),
      dealname: payload?.dealname || '',
      product: payload?.program || '',
      program: payload?.program || '',
      status: payload?.status || 'To Pursue',
      stage: payload?.status || 'To Pursue',
      owner: payload?.owner || '',
      ownerId: payload?.ownerId || '',
      won: false,
      lost: false,
      pursuitDate: payload?.pursuitDate ? new Date(`${payload.pursuitDate}T00:00:00`) : new Date(),
      wonDate: null,
      amount: null,
    },
    partnershipCount: 1,
    product: payload?.program || '',
    rep: payload?.owner || '',
    pursuitDate: payload?.pursuitDate ? new Date(`${payload.pursuitDate}T00:00:00`) : new Date(),
  }
}
